/**
 * Fixture permission-matrix state for the U-16-g2 editor (`GET`/`PUT /ops/v1/permissions`).
 *
 * This module is a COPY of two server tables and of one server rule, and it says so in every name —
 * the same deal `fixtures/identity.ts` struck for the settings whitelist, guarded the same way by
 * `test/matrixFixture.test.ts`:
 *
 *  - `FIXTURE_DEFAULT_LEVELS` mirrors `acs_service/identity.py::_DEFAULT_LEVELS` cell for cell, in the
 *    same role order (owner, admin, operator — `FIXTURE_ROLES` — then user), and
 *    `FIXTURE_LEVELS` mirrors `identity.py::LEVELS`. A guard that only checked the level ALPHABET would
 *    let a drifted cell slip; the guard compares the whole table both directions (a relation, no
 *    literal counts — G-DOC-۱۱).
 *  - `LOCKED_TO_OWNER` and `MATRIX_AUDIT_KIND` mirror the server's names of the same things
 *    (`identity.py::LOCKED_TO_OWNER`, `identity.py::MATRIX_AUDIT_KIND`).
 *  - `validateMatrixWrite` is a port of `identity.validate_matrix_write` — the three refusals in the
 *    server's order (vocabulary per cell, then §۳-۱ per cell, then §۳-۳ on the RESULTING matrix), with
 *    the door's sentences rendered from the same fragments (test/matrixFixture.test.ts checks every
 *    static fragment appears verbatim in the Python source). `matrixAcceptedDetail` is the `detail`
 *    sentence of `routers/identity.py::write_permission_matrix`, moved count included, because "a write
 *    that re-states the level a cell already holds is a successful no-op and must be allowed to say so".
 *
 * What is deliberately NOT here: any per-user shape. The wire vocabulary is capability × ROLE × level
 * (`models.py::MatrixCell`), §۱۲ records the per-user override as rejected, and a fixture that accepted
 * `role: "user-owner-demo"` would be teaching the page a door the server does not have — that target is
 * refused, with `identity.py:425-427`'s own sentence, by the ported validation. The same holds for a
 * machine scope named as a capability: scopes are not registry keys, and `identity.py:421-423` answers
 * — ported here as `registryCellRefusal`.
 */

import type { MatrixCellWrite } from '../api/types';
import { FIXTURE_ROLES } from './identity';

/** The capability the two matrix doors stand behind — `routers/identity.py:83`. */
export const POLICY_MATRIX_CAPABILITY = 'policy.matrix';

/** `identity.py::LEVELS` — the matrix alphabet. `none` IS spoken here, unlike the effective door's wire. */
export const FIXTURE_LEVELS = ['none', 'read', 'edit'] as const;

/** `identity.py::MATRIX_AUDIT_KIND` — the kind one accepted matrix write records in the ledger. */
export const MATRIX_AUDIT_KIND = 'permission.change';

/** `identity.py::LOCKED_TO_OWNER` (§۳-۱) — the two keys the matrix may never move off the owner. */
export const LOCKED_TO_OWNER = ['policy.matrix', 'user.manage'] as const;

/**
 * `identity.py::_DEFAULT_LEVELS`, copied in the source's declaration order, one row per registry key:
 * the four levels are owner / admin / operator / user — the `FIXTURE_ROLES` order, which is
 * `identity.py::ROLES` itself. `test/matrixFixture.test.ts` re-reads the Python file and fails on any
 * drift, cell for cell, key set both directions.
 */
export const FIXTURE_DEFAULT_LEVELS: Readonly<
  Record<string, readonly [string, string, string, string]>
> = {
  'device.params.read': ['edit', 'read', 'read', 'read'],
  'device.params.set': ['edit', 'edit', 'edit', 'none'],
  'device.params.raw': ['edit', 'edit', 'none', 'none'],
  'device.discover': ['edit', 'edit', 'read', 'none'],
  'fleet.devices.read': ['edit', 'read', 'read', 'read'],
  'fleet.devices.bind': ['edit', 'edit', 'none', 'none'],
  'fleet.tags.apply': ['edit', 'edit', 'read', 'none'],
  'fleet.audit.read': ['edit', 'read', 'none', 'none'],
  'activation.run': ['edit', 'edit', 'edit', 'none'],
  'activation.read': ['edit', 'read', 'read', 'none'],
  'device.replace': ['edit', 'edit', 'none', 'none'],
  'diagnostics.run': ['edit', 'edit', 'edit', 'none'],
  'plant.read': ['edit', 'read', 'read', 'none'],
  'optical.read': ['edit', 'read', 'read', 'none'],
  'alarm.read': ['edit', 'read', 'read', 'read'],
  'alarm.ack': ['edit', 'edit', 'edit', 'none'],
  'firmware.read': ['edit', 'read', 'read', 'none'],
  'firmware.write': ['edit', 'edit', 'none', 'none'],
  'campaign.read': ['edit', 'read', 'read', 'none'],
  'campaign.write': ['edit', 'edit', 'none', 'none'],
  'jobs.read': ['edit', 'read', 'read', 'read'],
  'jobs.cancel': ['edit', 'edit', 'edit', 'none'],
  'templates.read': ['edit', 'read', 'none', 'none'],
  'reconciliation.read': ['edit', 'read', 'read', 'none'],
  'settings.read': ['edit', 'read', 'read', 'none'],
  'settings.thresholds': ['edit', 'edit', 'none', 'none'],
  'profile.read': ['edit', 'read', 'read', 'none'],
  'profile.activate': ['edit', 'none', 'none', 'none'],
  'keys.read': ['edit', 'none', 'none', 'none'],
  'health.read': ['edit', 'read', 'read', 'read'],
  'events.read': ['edit', 'read', 'read', 'none'],
  'policy.matrix': ['edit', 'none', 'none', 'none'],
  'user.manage': ['edit', 'none', 'none', 'none'],
  'admin.retention.run': ['edit', 'none', 'none', 'none'],
  'ops.outbox.read': ['edit', 'read', 'read', 'none'],
  'ops.outbox.redrive': ['edit', 'edit', 'edit', 'none'],
  'admin.migrations.apply': ['edit', 'none', 'none', 'none'],
  'admin.backups.verify': ['edit', 'none', 'none', 'none'],
  // S1 (G-SUB-01/03) — circuit S's three keys. `subscriber.read` is E/E/R/R: the sixth read of
  // the `user` role, and safe to grant because the door masks the identifying fields for a
  // READ-level holder. `subscriber.manage` and `service.profile` are E/E/E/—.
  'subscriber.read': ['edit', 'edit', 'read', 'read'],
  'subscriber.manage': ['edit', 'edit', 'edit', 'none'],
  'service.profile': ['edit', 'edit', 'edit', 'none'],
  // N5 (G-PRC-10): the incident doors' key — `alarm.ack`'s grades, for the row that
  // groups alarms. Reading an incident is `alarm.read` (§۱'s contraction rule), so the
  // only new key is the write one.
  'incident.manage': ['edit', 'edit', 'edit', 'none'],
  // N6 (G-ALM-08): the notification channels — the incident grades, for the
  // surface that tells people about the alarms. `user` stays absent (= none).
  'notify.manage': ['edit', 'edit', 'edit', 'none'],
};

/** One matrix grid: capability → role → level, exactly the shape `effective_matrix` publishes. */
export type MatrixGrid = Record<string, Record<string, string>>;

/**
 * `identity.py::seed_default_matrix` + `effective_matrix` over the default table: every registry key,
 * every published role, no absent cell (§۸-۱: a key with a missing role value is a red test, and the
 * fixture would be the one failing it).
 */
export function buildFixtureMatrix(): MatrixGrid {
  const grid: MatrixGrid = {};
  for (const [capability, levels] of Object.entries(FIXTURE_DEFAULT_LEVELS)) {
    const row: Record<string, string> = {};
    FIXTURE_ROLES.forEach((role, index) => {
      row[role] = levels[index] as string;
    });
    grid[capability] = row;
  }
  return grid;
}

/** A fresh session copy — provider-local writes must not move the module fixture. */
export function cloneMatrix(grid: MatrixGrid): MatrixGrid {
  const copy: MatrixGrid = {};
  for (const [capability, row] of Object.entries(grid)) copy[capability] = { ...row };
  return copy;
}

/**
 * `identity.py::read_sibling`, ported: the registry's own read key for a capability, when one exists.
 * Derived from the naming the registry carries (`firmware.write` reads as `firmware.read`), not from a
 * second hand-kept table — so §۳-۳ can only judge what the vocabulary names.
 */
export function readSibling(capability: string, registry: readonly string[]): string | null {
  const prefix = capability.slice(0, capability.lastIndexOf('.'));
  const sibling = `${prefix}.read`;
  return registry.includes(sibling) ? sibling : null;
}

/** `identity.py:421-423` — a cell naming a capability outside the registry (a machine scope would land here). */
export function registryCellRefusal(capability: string): string {
  return `'${capability}' is not a capability in the registry; the cell cannot be written`;
}

/** `identity.py:425-427` — a cell targeting anything that is not one of the four roles (a per-user override would land here). */
export function matrixRoleInvalidDetail(role: string): string {
  return `role must be one of ${FIXTURE_ROLES.join(', ')}, got '${role}'`;
}

/** `identity.py:429-431` — a level outside the alphabet. */
export function matrixLevelInvalidDetail(level: string): string {
  return `level must be one of ${FIXTURE_LEVELS.join(', ')}, got '${level}'`;
}

/** `identity.py:440-443` (§۳-۱) — the matrix can never hand a locked key to another role. */
export function lockedGiveRefusal(capability: string, role: string): string {
  return `${capability} is locked to the owner (§۳-۱): the matrix can never give it to ${role}`;
}

/** `identity.py:444-448` (§۳-۱) — the locked key cannot be lowered for the owner either. */
export function lockedLowerRefusal(capability: string): string {
  return `${capability} is what lets the owner edit the matrix at all (§۳-۱); it cannot be lowered for the owner`;
}

/** `identity.py:463-466` (§۳-۳) — there is no edit without read in this vocabulary. */
export function noEditWithoutReadDetail(role: string, capability: string, sibling: string): string {
  return (
    `§۳-۳: ${role} would hold edit on ${capability} while holding none on ${sibling} — ` +
    'there is no edit without read in this vocabulary'
  );
}

/** One judged cell: what was asked, and the level it held before (§۶'s before/after audit pair). */
export interface ResolvedMatrixCell {
  capability: string;
  role: string;
  level: string;
  before: string;
}

/**
 * The door's answer to a refused write: the problem CODE and its sentence.
 * `policy-conflict` is 409 on the wire (§۳-۱), `reference-invalid` is 422 (vocabulary and §۳-۳) —
 * the same two codes `routers/identity.py` raises, never a UI paraphrase.
 */
export type MatrixRefusal =
  | { readonly ok: false; readonly code: 'reference-invalid'; readonly detail: string }
  | { readonly ok: false; readonly code: 'policy-conflict'; readonly detail: string };

/**
 * `identity.validate_matrix_write`, ported — all three refusals, in the server's order, judged BEFORE
 * anything is applied. A refusal means the grid does not move and no audit line is written, exactly as
 * the door's docstring promises; the fixture therefore never mutates on this path.
 *
 * The page does NOT call this to prevent attempts: the console mirrors, and the real judgment is the
 * server's. It lives here because the fixture is the demo's server, and a demo that accepted what the
 * door refuses would let the page pass on an answer the façade never sends.
 */
export function validateMatrixWrite(
  grid: MatrixGrid,
  cells: readonly MatrixCellWrite[],
): { ok: true; resolved: ResolvedMatrixCell[] } | MatrixRefusal {
  const registry = Object.keys(grid);
  const resolved: ResolvedMatrixCell[] = [];
  for (const cell of cells) {
    if (!registry.includes(cell.capability)) {
      return { ok: false, code: 'reference-invalid', detail: registryCellRefusal(cell.capability) };
    }
    if (!(FIXTURE_ROLES as readonly string[]).includes(cell.role)) {
      return { ok: false, code: 'reference-invalid', detail: matrixRoleInvalidDetail(cell.role) };
    }
    if (!(FIXTURE_LEVELS as readonly string[]).includes(cell.level)) {
      return { ok: false, code: 'reference-invalid', detail: matrixLevelInvalidDetail(cell.level) };
    }
    resolved.push({ ...cell, before: grid[cell.capability]?.[cell.role] ?? 'none' });
  }

  // §۳-۱ — judged per requested cell, before anything is applied.
  for (const cell of resolved) {
    if (!(LOCKED_TO_OWNER as readonly string[]).includes(cell.capability)) continue;
    if (cell.role !== 'owner' && cell.level !== 'none') {
      return {
        ok: false,
        code: 'policy-conflict',
        detail: lockedGiveRefusal(cell.capability, cell.role),
      };
    }
    if (cell.role === 'owner' && cell.level !== 'edit') {
      return { ok: false, code: 'policy-conflict', detail: lockedLowerRefusal(cell.capability) };
    }
  }

  // §۳-۳ — judged on the RESULTING matrix, so a batch of legal cells cannot launder an illegal pair.
  const resulting = cloneMatrix(grid);
  for (const cell of resolved) {
    (resulting[cell.capability] ??= {})[cell.role] = cell.level;
  }
  for (const capability of registry) {
    const sibling = readSibling(capability, registry);
    if (sibling === null) continue;
    for (const role of FIXTURE_ROLES) {
      if (resulting[capability]?.[role] !== 'edit') continue;
      if ((resulting[sibling]?.[role] ?? 'none') !== 'none') continue;
      return {
        ok: false,
        code: 'reference-invalid',
        detail: noEditWithoutReadDetail(role, capability, sibling),
      };
    }
  }
  return { ok: true, resolved };
}

/**
 * `routers/identity.py::write_permission_matrix`'s `detail`, ported: the count that MOVED, the caller
 * the door named, and — only when they differ — the count of cells that already held the level. "A
 * write that re-states the level a cell already holds is a successful no-op and must be allowed to say
 * so"; the editor renders this string, never an «applied» of its own (invariant ۳ / D6).
 */
export function matrixAcceptedDetail(moved: number, totalCells: number, subject: string): string {
  const restated = totalCells - moved;
  return (
    `${moved} matrix cell(s) updated by ${subject}` +
    (moved !== totalCells ? `; ${restated} already held the requested level` : '')
  );
}
